"use client"

import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CountryFlag } from '@/lib/countryFlags'
import { useTranslation } from 'react-i18next'
import type { AccordionRegion } from '@/lib/locationRegions'
import {
  countrySentinelForIso,
  isCountrySentinel,
  parseCountrySentinel,
} from '@/lib/locationRegions'

type LocationListDensity = 'default' | 'compact'

type BaseProps = {
  regions: AccordionRegion[]
  allLocationsText: string
  /** `compact` matches filter sheet dropdown typography (14px). */
  density?: LocationListDensity
}

function locationListLabelClasses(density: LocationListDensity, variant: 'country' | 'city'): string {
  if (density === 'compact') {
    return variant === 'country'
      ? 'text-sm font-medium text-foreground flex-1 min-w-0 text-left truncate'
      : 'text-sm font-normal text-foreground flex-1 min-w-0 text-left truncate'
  }
  return variant === 'country'
    ? 'text-base font-semibold text-foreground flex-1 min-w-0 text-left truncate'
    : 'text-base font-medium text-foreground flex-1 min-w-0 text-left truncate'
}

function locationListRowPaddingClass(density: LocationListDensity): string {
  return density === 'compact' ? 'py-2' : 'py-3'
}

export type SingleLocationRegionsProps = BaseProps & {
  multiSelect?: false
  selectedValue: string
  onSelect: (value: string) => void
}

export type MultiLocationRegionsProps = BaseProps & {
  multiSelect: true
  selectedCities: string[]
  onSelectionChange: (cities: string[]) => void
}

export type LocationRegionsFlatListProps = SingleLocationRegionsProps | MultiLocationRegionsProps

function selectedCityOnly(
  selectedValue: string | undefined,
  allLocationsText: string
): string | undefined {
  if (!selectedValue || selectedValue === allLocationsText || isCountrySentinel(selectedValue)) {
    return undefined
  }
  return selectedValue
}

function regionMatchingSelection(
  regions: AccordionRegion[],
  selectedValue: string,
  allLocationsText: string
): AccordionRegion | null {
  if (!selectedValue || selectedValue === allLocationsText) return null
  if (isCountrySentinel(selectedValue)) {
    const iso = parseCountrySentinel(selectedValue)
    if (!iso) return null
    return regions.find((r) => r.iso2?.toUpperCase() === iso) ?? null
  }
  return regions.find((r) => r.cities.includes(selectedValue)) ?? null
}

function LocationRegionsFlatListSingle({
  regions,
  selectedValue,
  allLocationsText,
  onSelect,
  density = 'default',
}: SingleLocationRegionsProps) {
  const rowPaddingClass = locationListRowPaddingClass(density)
  const { t } = useTranslation()
  const activeCityOnly = React.useMemo(
    () => selectedCityOnly(selectedValue, allLocationsText),
    [selectedValue, allLocationsText]
  )

  const [expandedKey, setExpandedKey] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!regions.length || !selectedValue || selectedValue === allLocationsText) {
      setExpandedKey(null)
      return
    }
    const match = regionMatchingSelection(regions, selectedValue, allLocationsText)
    if (match) setExpandedKey(match.key)
  }, [regions, selectedValue, allLocationsText])

  return (
    <div className="space-y-0">
      {regions.map((region) => {
        const locksOpenDueToCity = !!(activeCityOnly && region.cities.includes(activeCityOnly))
        const isExpanded = locksOpenDueToCity || expandedKey === region.key

        return (
          <div key={region.key} className="border-b border-border/30 last:border-b-0">
            <div className={`flex items-start gap-1 ${rowPaddingClass}`}>
              <button
                type="button"
                disabled={locksOpenDueToCity}
                className={`shrink-0 p-2 -ml-2 -my-1 rounded-lg text-muted-foreground transition-colors ${
                  locksOpenDueToCity
                    ? 'opacity-40 cursor-default'
                    : 'hover:bg-muted/50 hover:text-foreground'
                }`}
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? t('filters.hideCitiesAria', 'Hide cities')
                    : t('filters.showCitiesAria', 'Show cities')
                }
                onClick={(e) => {
                  e.stopPropagation()
                  if (locksOpenDueToCity) return
                  setExpandedKey((k) => (k === region.key ? null : region.key))
                }}
              >
                <ChevronDown
                  className={`w-[18px] h-[18px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {region.iso2 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto flex-1 min-w-0 justify-start px-2 py-1 -my-1 hover:bg-transparent hover:opacity-[0.97]"
                  onClick={() => onSelect(countrySentinelForIso(region.iso2!))}
                >
                  <div className="flex items-center gap-3 w-full min-w-0 text-left flex-wrap">
                    <CountryFlag
                      country={region.countryName}
                      size="sm"
                      showBorder={false}
                      className="shrink-0 !rounded-sm"
                    />
                    <span className={locationListLabelClasses(density, 'country')}>
                      {region.countryName}
                    </span>
                    <Checkbox
                      checked={
                        selectedValue !== allLocationsText &&
                        isCountrySentinel(selectedValue) &&
                        selectedValue === countrySentinelForIso(region.iso2!)
                      }
                      className="pointer-events-none shrink-0 ml-2"
                    />
                  </div>
                </Button>
              ) : (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 flex-1 min-w-0 px-2 py-1 pt-2">
                  <CountryFlag
                    country={region.countryName}
                    size="sm"
                    showBorder={false}
                    className="shrink-0 !rounded-sm"
                  />
                  <span className={locationListLabelClasses(density, 'country')}>{region.countryName}</span>
                </div>
              )}
            </div>

            {isExpanded ? (
              <div className="pb-2 pt-0">
                {region.cities.map((city) => {
                  const countryCoversAllCities =
                    !!region.iso2 &&
                    isCountrySentinel(selectedValue) &&
                    selectedValue === countrySentinelForIso(region.iso2)
                  const citySelected = selectedValue === city || countryCoversAllCities
                  return (
                    <div key={city} className={`flex items-start gap-1 ${rowPaddingClass}`}>
                      <div
                        className="shrink-0 p-2 -ml-2 -my-1 pointer-events-none select-none"
                        aria-hidden
                      >
                        <ChevronDown className="w-[18px] h-[18px] text-transparent" />
                      </div>
                      <button
                        type="button"
                        className="h-auto flex-1 min-w-0 justify-start px-2 py-1 -my-1 rounded-lg border-0 bg-transparent shadow-none outline-none cursor-pointer text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                        onClick={() => onSelect(city)}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0 text-left">
                          <span
                            className="shrink-0 rounded-sm bg-transparent w-3.5 h-3.5"
                            aria-hidden
                          />
                          <span className={locationListLabelClasses(density, 'city')}>
                            {city}
                          </span>
                          <Checkbox
                            checked={citySelected}
                            className="pointer-events-none shrink-0 ml-2"
                          />
                        </div>
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function LocationRegionsFlatListMulti({
  regions,
  selectedCities,
  onSelectionChange,
  density = 'default',
}: MultiLocationRegionsProps) {
  const rowPaddingClass = locationListRowPaddingClass(density)
  const { t } = useTranslation()
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null)

  const toggleCity = (city: string) => {
    if (selectedCities.includes(city)) {
      onSelectionChange(selectedCities.filter((c) => c !== city))
    } else {
      onSelectionChange([...selectedCities, city])
    }
  }

  return (
    <div className="space-y-0">
      {regions.map((region) => {
        const isExpanded = expandedKey === region.key
        const selectedInRegion = region.cities.filter((c) => selectedCities.includes(c))
        const allSelected =
          region.cities.length > 0 && selectedInRegion.length === region.cities.length
        const someSelected = selectedInRegion.length > 0 && !allSelected

        const countryCheckState: boolean | 'indeterminate' = allSelected
          ? true
          : someSelected && !isExpanded
            ? 'indeterminate'
            : false

        const toggleExpanded = () =>
          setExpandedKey((k) => (k === region.key ? null : region.key))

        return (
          <div key={region.key} className="border-b border-border/30 last:border-b-0">
            <div className={`flex items-start gap-1 ${rowPaddingClass}`}>
              <button
                type="button"
                className="shrink-0 p-2 -ml-2 -my-1 rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                aria-expanded={isExpanded}
                aria-label={
                  isExpanded
                    ? t('filters.hideCitiesAria', 'Hide cities')
                    : t('filters.showCitiesAria', 'Show cities')
                }
                onClick={(e) => {
                  e.stopPropagation()
                  toggleExpanded()
                }}
              >
                <ChevronDown
                  className={`w-[18px] h-[18px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {region.iso2 ? (
                <div className="flex flex-1 min-w-0 items-start gap-0">
                  <button
                    type="button"
                    className="h-auto flex-1 min-w-0 justify-start px-2 py-1 -my-1 rounded-lg border-0 bg-transparent shadow-none outline-none cursor-pointer text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                    onClick={toggleExpanded}
                  >
                    <div className="flex items-center gap-3 w-full min-w-0 text-left flex-wrap">
                      <CountryFlag
                        country={region.countryName}
                        size="sm"
                        showBorder={false}
                        className="shrink-0 !rounded-sm"
                      />
                      <span className={locationListLabelClasses(density, 'country')}>
                        {region.countryName}
                      </span>
                    </div>
                  </button>
                  <div className="shrink-0 self-center pt-0.5 pr-0">
                    <Checkbox
                      checked={countryCheckState}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={(v) => {
                        if (v === true) {
                          onSelectionChange(
                            [...new Set([...selectedCities, ...region.cities])].sort((a, b) =>
                              a.localeCompare(b)
                            )
                          )
                        } else {
                          onSelectionChange(
                            selectedCities.filter((c) => !region.cities.includes(c))
                          )
                        }
                      }}
                      className="shrink-0 ml-2"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 flex-1 min-w-0 px-2 py-1 pt-2">
                  <CountryFlag
                    country={region.countryName}
                    size="sm"
                    showBorder={false}
                    className="shrink-0 !rounded-sm"
                  />
                  <span className={locationListLabelClasses(density, 'country')}>{region.countryName}</span>
                </div>
              )}
            </div>

            {isExpanded ? (
              <div className="pb-2 pt-0">
                {region.cities.map((city) => {
                  const citySelected = selectedCities.includes(city)
                  return (
                    <div key={city} className={`flex items-start gap-1 ${rowPaddingClass}`}>
                      <div
                        className="shrink-0 p-2 -ml-2 -my-1 pointer-events-none select-none"
                        aria-hidden
                      >
                        <ChevronDown className="w-[18px] h-[18px] text-transparent" />
                      </div>
                      <button
                        type="button"
                        className="h-auto flex-1 min-w-0 justify-start px-2 py-1 -my-1 rounded-lg border-0 bg-transparent shadow-none outline-none cursor-pointer text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                        onClick={() => toggleCity(city)}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0 text-left">
                          <span
                            className="shrink-0 rounded-sm bg-transparent w-3.5 h-3.5"
                            aria-hidden
                          />
                          <span className={locationListLabelClasses(density, 'city')}>
                            {city}
                          </span>
                          <Checkbox
                            checked={citySelected}
                            className="pointer-events-none shrink-0 ml-2"
                          />
                        </div>
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export function LocationRegionsFlatList(props: LocationRegionsFlatListProps) {
  if (props.multiSelect) {
    return <LocationRegionsFlatListMulti {...props} />
  }
  return <LocationRegionsFlatListSingle {...props} />
}
